import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Auth API
export const authAPI = {
    login: async (username, password) => {
        const response = await axios.post(`${API_BASE}/auth/login`, { username, password });
        return response.data;
    },
    logout: async (token) => {
        const response = await axios.post(`${API_BASE}/auth/logout?token=${token}`);
        return response.data;
    },
    setup: async (token, newPassword, pin) => {
        const response = await axios.post(`${API_BASE}/auth/setup?token=${token}`, { new_password: newPassword, pin });
        return response.data;
    }
};

// Super Admin API
export const superAdminAPI = {
    getAdmins: async (token) => {
        const response = await axios.get(`${API_BASE}/super-admin/admins?token=${token}`);
        return response.data;
    },
    createAdmin: async (token, username, password) => {
        const response = await axios.post(`${API_BASE}/super-admin/admins?token=${token}`, { username, password });
        return response.data;
    },
    deleteAdmin: async (token, adminId) => {
        const response = await axios.delete(`${API_BASE}/super-admin/admins/${adminId}?token=${token}`);
        return response.data;
    },
    resetAdminPassword: async (token, adminId, newPassword) => {
        const response = await axios.post(`${API_BASE}/super-admin/admins/${adminId}/reset-password?token=${token}`, { new_password: newPassword });
        return response.data;
    },
    addAdminBalance: async (token, adminId, amount) => {
        const response = await axios.post(`${API_BASE}/super-admin/admins/${adminId}/balance?token=${token}`, { amount });
        return response.data;
    },
    getAllTransactions: async (token) => {
        const response = await axios.get(`${API_BASE}/super-admin/transactions?token=${token}`);
        return response.data;
    },
    getPublicSettings: async (token) => {
        const response = await axios.get(`${API_BASE}/super-admin/public-settings?token=${token}`);
        return response.data;
    },
    togglePublicLeaderboard: async (token) => {
        const response = await axios.put(`${API_BASE}/super-admin/public-settings/toggle?token=${token}`);
        return response.data;
    },
    toggleGroupPublic: async (token, groupId) => {
        const response = await axios.put(`${API_BASE}/super-admin/public-settings/group/${groupId}?token=${token}`);
        return response.data;
    }
};

// Admin API
export const adminAPI = {
    getUsers: async (token) => {
        const response = await axios.get(`${API_BASE}/admin/users?token=${token}`);
        return response.data;
    },
    createUser: async (token, username, password) => {
        const response = await axios.post(`${API_BASE}/admin/users?token=${token}`, { username, password });
        return response.data;
    },
    deleteUser: async (token, userId) => {
        const response = await axios.delete(`${API_BASE}/admin/users/${userId}?token=${token}`);
        return response.data;
    },
    resetUserPassword: async (token, userId, newPassword) => {
        const response = await axios.post(`${API_BASE}/admin/users/${userId}/reset-password?token=${token}`, { new_password: newPassword });
        return response.data;
    },
    toggleFinanceStatus: async (token, userId) => {
        const response = await axios.put(`${API_BASE}/admin/users/${userId}/finance?token=${token}`);
        return response.data;
    },
    toggleHiddenStatus: async (token, userId) => {
        const response = await axios.put(`${API_BASE}/admin/users/${userId}/hidden?token=${token}`);
        return response.data;
    },
    toggleHideZeroScores: async (token, groupId) => {
        const response = await axios.put(`${API_BASE}/admin/groups/${groupId}/hide-zero-scores?token=${token}`);
        return response.data;
    },
    toggleGroupPublic: async (token, groupId) => {
        const response = await axios.put(`${API_BASE}/admin/groups/${groupId}/toggle-public?token=${token}`);
        return response.data;
    },
    addUserBalance: async (token, userId, amount) => {
        const response = await axios.post(`${API_BASE}/admin/users/${userId}/balance?token=${token}`, { amount });
        return response.data;
    },
    getBalance: async (token) => {
        const response = await axios.get(`${API_BASE}/admin/balance?token=${token}`);
        return response.data;
    },
    getGroups: async (token) => {
        const response = await axios.get(`${API_BASE}/admin/groups?token=${token}`);
        return response.data;
    },
    createGroup: async (token, name) => {
        const response = await axios.post(`${API_BASE}/admin/groups?token=${token}`, { name });
        return response.data;
    },
    deleteGroup: async (token, groupId) => {
        const response = await axios.delete(`${API_BASE}/admin/groups/${groupId}?token=${token}`);
        return response.data;
    },
    addUserToGroup: async (token, groupId, userId) => {
        const response = await axios.post(`${API_BASE}/admin/groups/${groupId}/members?token=${token}`, { user_id: userId });
        return response.data;
    },
    removeUserFromGroup: async (token, groupId, userId) => {
        const response = await axios.delete(`${API_BASE}/admin/groups/${groupId}/members/${userId}?token=${token}`);
        return response.data;
    },
    getTransactions: async (token) => {
        const response = await axios.get(`${API_BASE}/admin/transactions?token=${token}`);
        return response.data;
    },
    exportCSV: async (token) => {
        const response = await axios.get(`${API_BASE}/admin/export/csv?token=${token}`, { responseType: 'blob' });
        return response.data;
    },
    // Tournament APIs
    getTournaments: async (token) => {
        const response = await axios.get(`${API_BASE}/admin/tournaments?token=${token}`);
        return response.data;
    },
    createTournament: async (token, name) => {
        const response = await axios.post(`${API_BASE}/admin/tournaments?token=${token}`, { name });
        return response.data;
    },
    deleteTournament: async (token, tournamentId) => {
        const response = await axios.delete(`${API_BASE}/admin/tournaments/${tournamentId}?token=${token}`);
        return response.data;
    },
    getTournamentRounds: async (token, tournamentId) => {
        const response = await axios.get(`${API_BASE}/admin/tournaments/${tournamentId}/rounds?token=${token}`);
        return response.data;
    },
    createTournamentRound: async (token, tournamentId, name) => {
        const response = await axios.post(`${API_BASE}/admin/tournaments/${tournamentId}/rounds?token=${token}`, { name });
        return response.data;
    },
    deleteTournamentRound: async (token, tournamentId, roundId) => {
        const response = await axios.delete(`${API_BASE}/admin/tournaments/${tournamentId}/rounds/${roundId}?token=${token}`);
        return response.data;
    },
    getTournamentGroups: async (token, roundId) => {
        const response = await axios.get(`${API_BASE}/admin/tournaments/rounds/${roundId}/groups?token=${token}`);
        return response.data;
    },
    createTournamentGroup: async (token, roundId, name, numGames = 3) => {
        const response = await axios.post(`${API_BASE}/admin/tournaments/rounds/${roundId}/groups?token=${token}`, { name, num_games: numGames });
        return response.data;
    },
    deleteTournamentGroup: async (token, groupId) => {
        const response = await axios.delete(`${API_BASE}/admin/tournaments/groups/${groupId}?token=${token}`);
        return response.data;
    },
    addTournamentGroupMember: async (token, groupId, userId) => {
        const response = await axios.post(`${API_BASE}/admin/tournaments/groups/${groupId}/members?token=${token}`, { user_id: userId });
        return response.data;
    },
    removeTournamentGroupMember: async (token, groupId, userId) => {
        const response = await axios.delete(`${API_BASE}/admin/tournaments/groups/${groupId}/members/${userId}?token=${token}`);
        return response.data;
    },
    getTournamentScores: async (token, groupId) => {
        const response = await axios.get(`${API_BASE}/admin/tournaments/groups/${groupId}/scores?token=${token}`);
        return response.data;
    },
    addTournamentScore: async (token, groupId, gameNumber, entries) => {
        const response = await axios.post(`${API_BASE}/admin/tournaments/groups/${groupId}/scores?token=${token}`, { game_number: gameNumber, entries });
        return response.data;
    },
    // Head-to-Head Statistics API
    getHeadToHead: async (token, groupId, playerIds) => {
        const response = await axios.post(`${API_BASE}/admin/groups/${groupId}/head-to-head?token=${token}`, { player_ids: playerIds });
        return response.data;
    },
    // Operator & TK Settings API
    getGroupSettings: async (token, groupId) => {
        const response = await axios.get(`${API_BASE}/admin/groups/${groupId}/settings?token=${token}`);
        return response.data;
    },
    updateOperators: async (token, groupId, operatorIds) => {
        const response = await axios.put(`${API_BASE}/admin/groups/${groupId}/operators?token=${token}`, { operator_ids: operatorIds });
        return response.data;
    },
    updateTKSettings: async (token, groupId, tkSettings) => {
        const response = await axios.put(`${API_BASE}/admin/groups/${groupId}/tk-settings?token=${token}`, tkSettings);
        return response.data;
    },
    // Settlements API
    getSettlements: async (token, groupId) => {
        const response = await axios.get(`${API_BASE}/groups/${groupId}/settlements?token=${token}`);
        return response.data;
    },
    createSettlement: async (token, groupId, entries, note = null) => {
        const response = await axios.post(`${API_BASE}/groups/${groupId}/settlements?token=${token}`, { entries, note });
        return response.data;
    },
    updateBulkBalance: async (token, groupId, userIds, amount, operation = 'set') => {
        const response = await axios.post(`${API_BASE}/groups/${groupId}/bulk-balance?token=${token}`, { user_ids: userIds, amount, operation });
        return response.data;
    },
    checkOperator: async (token, groupId) => {
        const response = await axios.get(`${API_BASE}/groups/${groupId}/check-operator?token=${token}`);
        return response.data;
    }
};

// User API
export const userAPI = {
    getProfile: async (token) => {
        const response = await axios.get(`${API_BASE}/user/profile?token=${token}`);
        return response.data;
    },
    getGroups: async (token) => {
        const response = await axios.get(`${API_BASE}/user/groups?token=${token}`);
        return response.data;
    },
    getBalance: async (token) => {
        const response = await axios.get(`${API_BASE}/user/balance?token=${token}`);
        return response.data;
    },
    transfer: async (token, toUserId, amount, pin) => {
        const response = await axios.post(`${API_BASE}/user/transfer?token=${token}`, { to_user_id: toUserId, amount, pin });
        return response.data;
    },
    getTransactions: async (token) => {
        const response = await axios.get(`${API_BASE}/user/transactions?token=${token}`);
        return response.data;
    },
    // Tournament APIs for Users (Viewing/Participating)
    getTournaments: async (token) => {
        const response = await axios.get(`${API_BASE}/user/tournaments?token=${token}`);
        return response.data;
    },
    getTournamentRounds: async (token, tournamentId) => {
        const response = await axios.get(`${API_BASE}/user/tournaments/${tournamentId}/rounds?token=${token}`);
        return response.data;
    },
    getTournamentGroups: async (token, roundId) => {
        const response = await axios.get(`${API_BASE}/user/tournaments/rounds/${roundId}/groups?token=${token}`);
        return response.data;
    },
    getTournamentScores: async (token, groupId) => {
        const response = await axios.get(`${API_BASE}/user/tournaments/groups/${groupId}/scores?token=${token}`);
        return response.data;
    },
    addTournamentScore: async (token, groupId, gameNumber, entries) => {
        const response = await axios.post(`${API_BASE}/user/tournaments/groups/${groupId}/scores?token=${token}`, { game_number: gameNumber, entries });
        return response.data;
    },
    // Tournament Management APIs for Users (Creating/Managing own tournaments)
    getMyTournaments: async (token) => {
        const response = await axios.get(`${API_BASE}/user/my-tournaments?token=${token}`);
        return response.data;
    },
    createTournament: async (token, name) => {
        const response = await axios.post(`${API_BASE}/user/tournaments?token=${token}`, { name });
        return response.data;
    },
    deleteTournament: async (token, tournamentId) => {
        const response = await axios.delete(`${API_BASE}/user/tournaments/${tournamentId}?token=${token}`);
        return response.data;
    },
    getMyTournamentRounds: async (token, tournamentId) => {
        const response = await axios.get(`${API_BASE}/user/tournaments/${tournamentId}/rounds-manage?token=${token}`);
        return response.data;
    },
    createTournamentRound: async (token, tournamentId, name) => {
        const response = await axios.post(`${API_BASE}/user/tournaments/${tournamentId}/rounds?token=${token}`, { name });
        return response.data;
    },
    deleteTournamentRound: async (token, tournamentId, roundId) => {
        const response = await axios.delete(`${API_BASE}/user/tournaments/${tournamentId}/rounds/${roundId}?token=${token}`);
        return response.data;
    },
    getMyTournamentGroups: async (token, roundId) => {
        const response = await axios.get(`${API_BASE}/user/tournaments/rounds/${roundId}/groups-manage?token=${token}`);
        return response.data;
    },
    createTournamentGroup: async (token, roundId, name, numGames) => {
        const response = await axios.post(`${API_BASE}/user/tournaments/rounds/${roundId}/groups?token=${token}`, { name, num_games: numGames });
        return response.data;
    },
    deleteTournamentGroup: async (token, groupId) => {
        const response = await axios.delete(`${API_BASE}/user/tournaments/groups/${groupId}?token=${token}`);
        return response.data;
    },
    updateTournamentGroup: async (token, groupId, name, numGames) => {
        const response = await axios.put(`${API_BASE}/user/tournaments/groups/${groupId}?token=${token}`, { name, num_games: numGames });
        return response.data;
    },
    addTournamentMember: async (token, groupId, userId) => {
        const response = await axios.post(`${API_BASE}/user/tournaments/groups/${groupId}/members?token=${token}`, { user_id: userId });
        return response.data;
    },
    removeTournamentMember: async (token, groupId, userId) => {
        const response = await axios.delete(`${API_BASE}/user/tournaments/groups/${groupId}/members/${userId}?token=${token}`);
        return response.data;
    },
    getAllUsers: async (token) => {
        const response = await axios.get(`${API_BASE}/user/all-users?token=${token}`);
        return response.data;
    },
    // Score Card APIs
    createScoreCard: async (token, groupId, entries, customDate = null, affectsBalance = false) => {
        const data = { group_id: groupId, entries, affects_balance: affectsBalance };
        if (customDate) data.custom_date = customDate;
        const response = await axios.post(`${API_BASE}/user/scorecards?token=${token}`, data);
        return response.data;
    },
    getScoreCards: async (token, groupId, month = null, year = null) => {
        let url = `${API_BASE}/user/scorecards?token=${token}&group_id=${groupId}`;
        if (month !== null && year !== null) {
            url += `&month=${month}&year=${year}`;
        }
        const response = await axios.get(url);
        return response.data;
    },
    updateScoreCard: async (token, scorecardId, entries) => {
        const response = await axios.put(`${API_BASE}/user/scorecards/${scorecardId}?token=${token}`, { entries });
        return response.data;
    },
    getLeaderboard: async (token, groupId, month = null, year = null) => {
        let url = `${API_BASE}/user/leaderboard?token=${token}&group_id=${groupId}`;
        if (month !== null && year !== null) {
            url += `&month=${month}&year=${year}`;
        }
        const response = await axios.get(url);
        return response.data;
    },
    getLeaderboardRange: async (token, groupId, startMonth, startYear, endMonth, endYear) => {
        const url = `${API_BASE}/user/leaderboard/range?token=${token}&group_id=${groupId}&start_month=${startMonth}&start_year=${startYear}&end_month=${endMonth}&end_year=${endYear}`;
        const response = await axios.get(url);
        return response.data;
    },
    getAvailableMonths: async (token, groupId) => {
        const response = await axios.get(`${API_BASE}/user/leaderboard/months?token=${token}&group_id=${groupId}`);
        return response.data;
    },
    toggleSettlement: async (token, scorecardId) => {
        const response = await axios.put(`${API_BASE}/user/scorecards/${scorecardId}/settle?token=${token}`);
        return response.data;
    },
    toggleFinanceSettled: async (token, scorecardId) => {
        const response = await axios.put(`${API_BASE}/user/scorecards/${scorecardId}/finance-settle?token=${token}`);
        return response.data;
    },
    
    // Telegram API
    getTelegramStatus: async (token) => {
        const response = await axios.get(`${API_BASE}/user/telegram/status?token=${token}`);
        return response.data;
    },
    generateTelegramLink: async (token) => {
        const response = await axios.post(`${API_BASE}/user/telegram/generate-link?token=${token}`);
        return response.data;
    },
    unlinkTelegram: async (token) => {
        const response = await axios.post(`${API_BASE}/user/telegram/unlink?token=${token}`);
        return response.data;
    },
    // Group Settings (TK info for users)
    getGroupSettings: async (token, groupId) => {
        const response = await axios.get(`${API_BASE}/user/groups/${groupId}/settings?token=${token}`);
        return response.data;
    }
};

// Admin Score Card API
export const adminScoreCardAPI = {
    getScoreCards: async (token, groupId, month = null, year = null) => {
        let url = `${API_BASE}/admin/scorecards?token=${token}&group_id=${groupId}`;
        if (month !== null && year !== null) {
            url += `&month=${month}&year=${year}`;
        }
        const response = await axios.get(url);
        return response.data;
    },
    createScoreCard: async (token, groupId, entries, month = null, year = null) => {
        const data = { group_id: groupId, entries };
        if (month !== null) data.month = month;
        if (year !== null) data.year = year;
        const response = await axios.post(`${API_BASE}/admin/scorecards?token=${token}`, data);
        return response.data;
    },
    updateScoreCard: async (token, scorecardId, entries) => {
        const response = await axios.put(`${API_BASE}/admin/scorecards/${scorecardId}?token=${token}`, { entries });
        return response.data;
    },
    deleteScoreCard: async (token, scorecardId) => {
        const response = await axios.delete(`${API_BASE}/admin/scorecards/${scorecardId}?token=${token}`);
        return response.data;
    },
    getDuplicates: async (token, groupId) => {
        const response = await axios.get(`${API_BASE}/admin/scorecards/duplicates/${groupId}?token=${token}`);
        return response.data;
    },
    deleteDuplicates: async (token, groupId) => {
        const response = await axios.delete(`${API_BASE}/admin/scorecards/duplicates/${groupId}?token=${token}`);
        return response.data;
    },
    getDailyChart: async (token, groupId, month = null, year = null) => {
        let url = `${API_BASE}/admin/scorecards/daily-chart/${groupId}?token=${token}`;
        if (month !== null && year !== null) {
            url += `&month=${month}&year=${year}`;
        }
        const response = await axios.get(url);
        return response.data;
    },
    getPerGameChart: async (token, groupId, month = null, year = null) => {
        let url = `${API_BASE}/admin/scorecards/per-game-chart/${groupId}?token=${token}`;
        if (month !== null && year !== null) {
            url += `&month=${month}&year=${year}`;
        }
        const response = await axios.get(url);
        return response.data;
    },
    getRankingChart: async (token, groupId, month = null, year = null) => {
        let url = `${API_BASE}/admin/scorecards/ranking-chart/${groupId}?token=${token}`;
        if (month !== null && year !== null) {
            url += `&month=${month}&year=${year}`;
        }
        const response = await axios.get(url);
        return response.data;
    },
    getPlayerStatistics: async (token, groupId, month = null, year = null) => {
        let url = `${API_BASE}/admin/player-statistics/${groupId}?token=${token}`;
        if (month !== null && year !== null) {
            url += `&month=${month}&year=${year}`;
        }
        const response = await axios.get(url);
        return response.data;
    }
};

// Public API (No Auth Required)
export const publicAPI = {
    getSettings: async () => {
        const response = await axios.get(`${API_BASE}/public/settings`);
        return response.data;
    },
    getGroups: async () => {
        const response = await axios.get(`${API_BASE}/public/groups`);
        return response.data;
    },
    getLeaderboard: async (groupId, month = null, year = null) => {
        let url = `${API_BASE}/public/leaderboard/${groupId}`;
        if (month !== null && year !== null) {
            url += `?month=${month}&year=${year}`;
        }
        const response = await axios.get(url);
        return response.data;
    },
    getAvailableMonths: async (groupId) => {
        const response = await axios.get(`${API_BASE}/public/leaderboard/${groupId}/months`);
        return response.data;
    },
    getDailyChart: async (groupId, month = null, year = null) => {
        let url = `${API_BASE}/public/daily-chart/${groupId}`;
        if (month !== null && year !== null) {
            url += `?month=${month}&year=${year}`;
        }
        const response = await axios.get(url);
        return response.data;
    },
    getPerGameChart: async (groupId, month = null, year = null) => {
        let url = `${API_BASE}/public/per-game-chart/${groupId}`;
        if (month !== null && year !== null) {
            url += `?month=${month}&year=${year}`;
        }
        const response = await axios.get(url);
        return response.data;
    },
    getRankingChart: async (groupId, month = null, year = null) => {
        let url = `${API_BASE}/public/ranking-chart/${groupId}`;
        if (month !== null && year !== null) {
            url += `?month=${month}&year=${year}`;
        }
        const response = await axios.get(url);
        return response.data;
    }
};

export default { authAPI, superAdminAPI, adminAPI, adminScoreCardAPI, userAPI, publicAPI };
